/*
 * jmlib-js - Portable JuggleMaster Library (JavaScript Version)
 * Version 2.0
 * (C) Per Johan Groland 2006-2016
 *
 * Based on JMLib 2.0, (C) Per Johan Groland and Gary Briggs
 *
 * Based on JuggleMaster Version 1.60
 * Copyright (c) 1995-1996 Ken Matsuoka
 *
 * You may redistribute and/or modify JMLib_js under the terms of the
 * Modified BSD License as published in various places online or in the
 * COPYING.jmlib file in the package you downloaded.
 *
 * This program is distributed in the hope that it will be useful, but
 * WITHOUT  ANY  WARRANTY;  without   even  the  implied  warranty  of
 * MERCHANTABILITY or  FITNESS FOR A PARTICULAR PURPOSE.   See the
 * Modified BSD License for more details.
 */

/* eslint-disable no-var, eqeqeq, no-constant-condition */
import styles from './styles';
import { patterns, patternIdx } from './patterns';
import Ball from './ball';
import Arm from './arm';
import Hand from './hand';
export default class JMLib {
	// Constants
	STATUS_NONE = 0;
	STATUS_PAUSE = 1;
	STATUS_JUGGLE = 2;

	KW = 0.25; // ?
	DW = 290; // ?

	X_AXIS_ACCURACY = 1024; // Set higher for larger screens
	MAX_BALLS = 630;
	MAX_MULTIPLEX = 71;
	MAX_COLUMN = 71 // Highest throw ?
	
	OBJECT_HAND = 0x01;
	OBJECT_UNDER = 0x02;
	OBJECT_MOVE = 0x04;
	OBJECT_MOVE2 = 0x08;

	SPEED_MAX = 2.0;
	SPEED_MIN = 0.1;
	SPEED_DEFAULT = 1.0;
	maxHeightRatio = 1.00;
	minHeightRatio = 0.04;
	defaultHeightRatio = 0.17;
	maxDwellRatio = 0.90;
	minDwellRatio = 0.10;
	defaultDwellRatio = 0.50;

	SCALING_METHOD_CLASSIC = true;
	SCALING_METHOD_DYNAMIC = false;

	constructor(error=(msg) => console.error(msg)) {
		this.error = error;
		// public variables

		// These are used to draw the animation
		// The figure
		this.arm = new Arm();
		this.handBall1 = new Ball();
		this.handBall2 = new Ball();
		this.hand1 = new Hand();
		this.hand2 = new Hand();
		// Array of all ball objects
		this.balls = new Array(this.MAX_BALLS).fill(0).map(i => new Ball());

		this.dpm = 0;
		this.gx_max = 0;
		this.gx_min = 0;
		this.gy_max = 0;
		this.gy_min = 0;
		this.imageWidth = 0;
		this.imageHeight = 0;
		this.status = 0;

		// protected variables
		this.bm1 = 0;
		this.arm_x = 0;
		this.arm_y = 0;
		this.hand_x = 0;
		this.hand_y = 0;
		this.horCenter = 0;
		this.verCenter = 0;
		this.base = 0;
		this.time_count = 0;
		this.time_period = 0;
		this.beep = 0;
		this.fpu = true;
		this.tw = 0;
		this.aw = 0;
		this.pmax = 0;

		// Matrix: max_column x max_multiplex
		this.pattern = new Array(this.MAX_COLUMN).fill(0).map(i => new Array(this.MAX_MULTIPLEX).fill(0));

		this.patts = new Array(this.MAX_COLUMN);
		this.pattw = 0;
		this.r = new Array(this.MAX_COLUMN * 2);
		this.smode = 0;
		this.high0 = new Array(this.MAX_BALLS + 1);
		this.high = new Array(this.MAX_BALLS + 1);
		this.kw0 = 0;       // XR/KW [m]

		this.siteswap = ""; // The current siteswap
		this.pattname = ""; // The name of the current pattern
		this.steps = new Array(this.MAX_COLUMN); // used to print the site on screen

		// Set default values / Initialize
		this.ga = 9.8; // How fast balls are thrown
		this.dwellRatio = 0.5;
		this.heightRatio = 0.20;
		this.mirror = 0;
		this.cSpeed = this.SPEED_DEFAULT
		this.synchronous = 0; // Flag: pattern synchronous or not
		this.hand_on = 1;
		this.hand_x = 0;
		this.hand_y = 0;
		this.setScalingMethod()
		this.smode = 50.0;
		this.status = this.STATUS_NONE;
		this.setPattern(); // Default pattern
	};
	get siteposStart() {
		if (this.synchronous && this.time_period % 2 == 1) {
			return this.steps[this.time_period - 1];
		}
		return this.steps[this.time_period];
	}
	get siteposStop () {
		if (this.synchronous && this.time_period % 2 == 0) {
			return this.steps[this.time_period + 2];
		}
		return this.steps[this.time_period + 1];
	};

	get siteposLen() {
		return this.getSiteposStop - this.getSiteposStart;
	};

	setWindowSize (width=480, height=400) {
		if (width <= 0 || height <= 0)
			return false;
		if (this.scalingMethod == this.SCALING_METHOD_DYNAMIC) {
			this.scaleImageWidth = width;
			this.scaleImageHeight = height;
			return true;
		}
		// store current status and stop juggling
		var oldStatus = this.status;
		this.stopJuggle();

		// set size
		this.imageWidth = width;
		this.imageHeight = height;
		this.horCenter = parseInt(this.imageWidth / 2);
		this.verCenter = parseInt(this.imageHeight / 2);

		// recalculate pattern
		if (oldStatus != this.STATUS_NONE) this.startJuggle();

		// restore state
		this.status = oldStatus;

		return true;
	};
	setScalingMethod(scalingMethod=this.SCALING_METHOD_CLASSIC) {
		// no change
		if (this.scalingMethod == scalingMethod) return;

		if (scalingMethod == this.SCALING_METHOD_DYNAMIC) {
			this.scaleImageWidth = this.imageWidth;
			this.scaleImageHeight = this.imageHeight;
			this.setWindowSize();
			this.scalingMethod = this.SCALING_METHOD_DYNAMIC;
		}
		else { //SCALING_METHOD_CLASSIC
			this.scalingMethod = this.SCALING_METHOD_CLASSIC;
			this.setWindowSize(this.scaleImageWidth, this.scaleImageHeight);
		}
	};

	get ballRadius() {
		var baseRadius = parseInt(11 * this.dpm / this.DW);

		if (this.scalingMethod == this.SCALING_METHOD_CLASSIC) {
			return baseRadius;
		}
		else {
			var zoomFactorY = this.scaleImageHeight / this.imageHeight;
			zoomFactorY *= 0.9;

			if (this.scaleImageWidth < this.scaleImageHeight)
				zoomFactorY *= (this.scaleImageWidth / this.scaleImageHeight);

			return parseInt(baseRadius * zoomFactorY); // convert return value to int
		}
	};

	doCoordTransform () {
		var i;
		var zoomFactorX = this.scaleImageWidth / this.imageWidth;
		var zoomFactorY = this.scaleImageHeight / this.imageHeight;

		// adjust for aspect ratio
		if (this.scaleImageWidth < this.scaleImageHeight)
			zoomFactorY *= this.scaleImageWidth / this.scaleImageHeight;
		else
			zoomFactorX *= this.scaleImageHeight / this.scaleImageWidth;

		zoomFactorX *= 1.1;
		zoomFactorY *= 0.9;

		// Adjust coordinates
		// head
		this.arm.hx -= parseInt(this.imageWidth / 2);
		this.arm.hy -= parseInt(this.imageHeight / 2);

		this.arm.hx = parseInt(this.arm.hx * zoomFactorX);
		this.arm.hy = parseInt(this.arm.hy * zoomFactorY);
		this.arm.hr = parseInt(this.arm.hr * zoomFactorY);

		this.arm.hx += parseInt(this.scaleImageWidth / 2);
		this.arm.hy += parseInt(this.scaleImageHeight / 2);

		// juggler
		for (i = 0; i < 6; i++) {
			this.arm.rx[i] -= parseInt(this.imageWidth / 2);
			this.arm.ry[i] -= parseInt(this.imageHeight / 2);
			this.arm.rx[i] = parseInt(this.arm.rx[i] * zoomFactorX);
			this.arm.ry[i] = parseInt(this.arm.ry[i] * zoomFactorY);
			this.arm.rx[i] += parseInt(this.scaleImageWidth / 2);
			this.arm.ry[i] += parseInt(this.scaleImageHeight / 2);

			this.arm.lx[i] -= parseInt(this.imageWidth / 2);
			this.arm.ly[i] -= parseInt(this.imageHeight / 2);
			this.arm.lx[i] = parseInt(this.arm.lx[i] * zoomFactorX);
			this.arm.ly[i] = parseInt(this.arm.ly[i] * zoomFactorY);
			this.arm.lx[i] += parseInt(this.scaleImageWidth / 2);
			this.arm.ly[i] += parseInt(this.scaleImageHeight / 2);
		}

		// hands
		this.handBall1.gx -= parseInt(this.imageWidth / 2);
		this.handBall1.gy -= parseInt(this.imageHeight / 2);
		this.handBall1.gx = parseInt(this.handBall1.gx * zoomFactorX);
		this.handBall1.gy = parseInt(this.handBall1.gy * zoomFactorY);
		this.handBall1.gx += parseInt(this.scaleImageWidth / 2);
		this.handBall1.gy += parseInt(this.scaleImageHeight / 2);

		this.handBall2.gx -= parseInt(this.imageWidth / 2);
		this.handBall2.gy -= parseInt(this.imageHeight / 2);
		this.handBall2.gx = parseInt(this.handBall2.gx * zoomFactorX);
		this.handBall2.gy = parseInt(this.handBall2.gy * zoomFactorY);
		this.handBall2.gx += parseInt(this.scaleImageWidth / 2);
		this.handBall2.gy += parseInt(this.scaleImageHeight / 2);

		for (i = 0; i <= 9; i++) {
			this.hand1.rx[i] = parseInt(this.hand2.rx[i] * zoomFactorX);
			this.hand1.ry[i] = parseInt(this.hand2.ry[i] * zoomFactorY);
			this.hand1.lx[i] = parseInt(this.hand2.lx[i] * zoomFactorX);
			this.hand1.ly[i] = parseInt(this.hand2.ly[i] * zoomFactorY);
		}

		// balls
		for (i = this.ballNumber - 1; i >= 0; i--) {
			this.balls[i].gx -= parseInt(this.imageWidth / 2);
			this.balls[i].gy -= parseInt(this.imageHeight / 2);

			this.balls[i].gx = parseInt(this.balls[i].gx * zoomFactorX);
			this.balls[i].gy = parseInt(this.balls[i].gy * zoomFactorY);

			this.balls[i].gx += parseInt(this.scaleImageWidth / 2);
			this.balls[i].gy += parseInt(this.scaleImageHeight / 2);
		}
	};

	setMirror (mir) {
		// store current status and stop juggling
		var oldStatus = this.status;
		this.stopJuggle();

		// set mirror
		this.mirror = mir;

		// recalculate pattern
		if (oldStatus != this.STATUS_NONE) this.startJuggle();

		// restore state
		this.status = oldStatus;
	};
	setSiteswap(site) {
		this.siteswap = site;
	}
	setPatternEx (ex) {
		this.setPattern(patternIdx[ex]);
	}
	setPattern (name="3-Cascade") {
		const {site, style, heightRatio, dwellRatio} = patterns.get(name);
			this.siteswap = site;
			this.pattname = name;
			this.heightRatio = heightRatio;
			this.dwellRatio = dwellRatio;

			this.setStyle(style);

			// Turn off beep
			this.beep = 0;

			// Check ratios
			if (this.heightRatio < this.minHeightRatio || this.heightRatio > this.maxHeightRatio)
				this.heightRatio = this.defaultHeightRatio;
			if (this.dwellRatio < this.minDwellRatio || this.dwellRatio > this.maxDwellRatio)
				this.dwellRatio = this.defaultDwellRatio;

			// Set pattern
			if ((this.jml_errno = this.set_patt(site)) == 0) {
				this.doStepcalc();
				return true;
			}
			else {
				switch (this.jml_errno) {
					case 4:
						this.error("Syntax error");
						break;
					case 5:
						this.error("Invalid pattern");
						break;
					case 6:
						this.error("Invalid character in pattern");
						break;
					case 7:
						this.error("Synchronous number must be even");
						break;
					case 8:
						this.error("Max 6 balls may be multiplexed");
						break;
					case 9:
						this.error("Too many balls in pattern");
						break;
					case 10:
						this.error("Pattern too long");
						break;
					case 13:
						this.error("0 inside [] is invalid");
						break;
					default:
						this.error("Unexpected error");
						break;
				}

				return false;
			}
		};

	setStyle (name="Normal") {
		this.style = styles.get(name);
	};

	setHR (HR) {
		if (HR > this.maxHeightRatio) {
			this.heightRatio = this.maxHeightRatio;
		}
		else if (HR < this.minHeightRatio) {
			this.heightRatio = this.minHeightRatio;
		}
		else {
			this.heightRatio = HR;
		}
	};

	setDR (DR) {
		if (DR > this.maxDwellRatio) {
			this.dwellRatio = this.maxDwellRatio;
		}
		else if (DR < this.minDwellRatio) {
			this.dwellRatio = this.minDwellRatio;
		}
		else {
			this.dwellRatio = DR;
		}
	};

// Internal functions
	arm_line () {
		var mx, my, sx, sy;

		if (this.hand_on) { // only bother calculating if hands are drawn
			// Notes:
			//  * gx/gy may need to be replaced by gx0/gy0 if erasing old values
			//    the method used in the X11 version
			//  * JMWin uses 11*dpm/DW instead of 11, which causes incorrect
			//    hand placement for some reason.
			this.arm.rx[0] = this.handBall1.gx + 11 + this.arm_x;
			this.arm.ry[0] = this.handBall1.gy + 11 + this.arm_y;
			this.arm.lx[0] = this.handBall2.gx + 11 - this.arm_x;
			this.arm.ly[0] = this.handBall2.gy + 11 + this.arm_y;

			sx = parseInt(this.dpm * this.X_AXIS_ACCURACY / this.kw0);
			sy = this.base - parseInt(this.dpm / 3);

			this.arm.rx[1] = parseInt((this.arm.rx[0] + (this.horCenter + sx) * 2) / 3 + this.dpm / 12);
			this.arm.lx[1] = parseInt((this.arm.lx[0] + (this.horCenter - sx) * 2) / 3 - this.dpm / 12);
			this.arm.ry[1] = parseInt((this.arm.ry[0] + sy) / 2 + this.dpm / 8);
			this.arm.ly[1] = parseInt((this.arm.ly[0] + sy) / 2 + this.dpm / 8);

			this.arm.rx[2] = parseInt((this.arm.rx[1] + (this.horCenter + sx) * 3) / 4);
			this.arm.lx[2] = parseInt((this.arm.lx[1] + (this.horCenter - sx) * 3) / 4);
			this.arm.ry[2] = parseInt((this.arm.ry[1] + sy * 2) / 3 - this.dpm / 25);
			this.arm.ly[2] = parseInt((this.arm.ly[1] + sy * 2) / 3 - this.dpm / 25);

			this.arm.rx[3] = parseInt((this.arm.rx[2] + (this.horCenter + sx) * 2) / 3 - this.dpm / 13);
			this.arm.lx[3] = parseInt((this.arm.lx[2] + (this.horCenter - sx) * 2) / 3 + this.dpm / 13);
			this.arm.ry[3] = parseInt((this.arm.ry[2] + sy * 2) / 3 - this.dpm / 40);
			this.arm.ly[3] = parseInt((this.arm.ly[2] + sy * 2) / 3 - this.dpm / 40);

			mx = (this.arm.rx[3] + this.arm.lx[3]) / 2;
			my = (this.arm.ry[3] + this.arm.ly[3]) / 2;

			this.arm.rx[4] = parseInt((mx * 2 + this.arm.rx[3]) / 3);
			this.arm.lx[4] = parseInt((mx * 2 + this.arm.lx[3]) / 3);
			this.arm.ry[4] = parseInt((my * 2 + this.arm.ry[3]) / 3);
			this.arm.ly[4] = parseInt((my * 2 + this.arm.ly[3]) / 3);

			this.arm.hx = parseInt(mx);
			this.arm.hy = parseInt((my * 2 - this.dpm * 2 / 3 + this.base) / 3);
			this.arm.hr = parseInt(this.dpm / 11);

			this.arm.rx[5] = this.arm.hx + parseInt(this.dpm / 20);
			this.arm.lx[5] = this.arm.hx - parseInt(this.dpm / 20);
			this.arm.ry[5] = this.arm.hy + parseInt(this.dpm / 13);
			this.arm.ly[5] = parseInt(this.arm.ry[5]);
		}
	};

	applyCorrections () {
		// Correct ball coordinates
		for (var i = this.ballNumber - 1; i >= 0; i--) {
			this.balls[i].gx += this.bm1;
			this.balls[i].gy += this.bm1;
		}
	};

	hand_pos (c, h, x, z) {
		//fixme: for testing only, remove
		// x and z must be arrays with one element (pass-by-reference emulation)
		//if (!(x instanceof Array)) document.write("hand_pos assert failure (x)<br>");
		//if (!(z instanceof Array)) document.write("hand_pos assert failure (z)<br>");

		var a;

		if (this.mirror) {
			if (!this.synchronous && h) c--;
			if (c & 1) a = ((--c + h) % this.style.length) * 4 + 2;
			else a = ((c + h) % this.style.length) * 4;
		}
		else {
			if (!this.synchronous && !h) c--;
			if (c & 1) a = ((c - h) % this.style.length) * 4 + 2;
			else a = ((c + 1 - h) % this.style.length) * 4;
		}

		if (h) x[0] = this.style.data[a];
		else  x[0] = -this.style.data[a];

		z[0] = this.style.data[a + 1];
	};

	juggle (/*Ball*/ d) {
		var tp;
		var flag = 0;
		var h, t;
		var tpox = new Array(1);
		tpox[0] = 0;
		var rpox = new Array(1);
		rpox[0] = 0;
		var tpoz = new Array(1);
		tpoz[0] = 0;
		var rpoz = new Array(1);
		rpoz[0] = 0;
		var x = 0;
		var y = 0;

		// Save old values
		d.gx1 = d.gx0;
		d.gy1 = d.gy0;

		d.gx0 = d.gx;
		d.gy0 = d.gy;

		if (d.c < 0) {
			if (this.time_count >= -d.c * this.tw) d.c = -d.c;
		}

		while (true) {
			tp = this.time_count - this.tw * Math.abs(d.c);
			if (tp < this.aw) break;
			d.st &= ~this.OBJECT_UNDER;
			d.c0 = d.c;

			if (d.st & this.OBJECT_HAND) {
				d.c += 2;
				this.flag = 1;
			}
			else {
				t = d.c;

				if (this.synchronous) {
					if (this.mirror && !d.chand) t++;
					else if (!this.mirror && d.chand) t++;
				}

				t %= this.pattw;
				d.bh = this.pattern[t][this.r[t]];
				d.c += Math.abs(d.bh);
				if (++this.r[t] >= this.patts[t]) this.r[t] = 0;

				d.thand = d.chand;
				if ((d.bh & 1) || d.bh < 0) d.chand = 1 - d.chand;

				flag = 1;
			}
		}

		if (d.c >= 0 && tp >= 0 && !(d.st & this.OBJECT_UNDER)) {
			d.st |= this.OBJECT_UNDER;

			if (d.st & this.OBJECT_HAND) {
				if (d.st & this.OBJECT_MOVE2) {
					d.st |= this.OBJECT_MOVE;
					d.st &= ~this.OBJECT_MOVE2;
				}
				else {
					d.st &= ~this.OBJECT_MOVE;
				}
			}
			else {
				t = d.c;

				if (this.synchronous) {
					if (this.mirror && !d.chand) t++;
					else if (!this.mirror && d.chand) t++;
				}

				t %= this.pattw;

				if (d.bh == 1) d.st |= this.OBJECT_MOVE;
				else d.st &= ~this.OBJECT_MOVE;

				for (var i = 0; i < this.patts[t]; i++) {
					h = this.pattern[t][i];
					if (h == 1) {
						if (d.chand) this.handBall2.st |= this.OBJECT_MOVE2;
						else this.handBall1.st |= this.OBJECT_MOVE2;
					}
					if (h != 2) {
						if (d.chand) this.handBall1.st |= this.OBJECT_MOVE2;
						else this.handBall2.st |= this.OBJECT_MOVE2;
						d.st |= this.OBJECT_MOVE;
					}
				}
			}
		}

		if (!(d.st & this.OBJECT_MOVE)) {
			if (d.c < 0) {
				//opera.postError("BEFORE hand_pos(" + -d.c + ", " + d.chand + ", " + tpox[0] + ", " + tpoz[0] + ")");
				this.hand_pos(-d.c, d.chand, tpox, tpoz);
				//opera.postError("AFTER hand_pos(" + -d.c + ", " + d.chand + ", " + tpox[0] + ", " + tpoz[0] + ")");
				rpox[0] = tpox[0];
				rpoz[0] = tpoz[0];
			}
			else {
				if (d.st & this.OBJECT_UNDER) {
					//opera.postError("BEFORE hand_pos(" + d.c + ", " + d.chand + ", " + tpox[0] + ", " + tpoz[0] + ")");
					this.hand_pos(d.c, d.chand, tpox, tpoz);
					//opera.postError("AFTER hand_pos(" + d.c + ", " + d.chand + ", " + tpox[0] + ", " + tpoz[0] + ")");

					//opera.postError("BEFORE hand_pos(" + (d.c + 2) + ", " + d.chand + ", " + rpox[0] + ", " + rpoz[0] + ")");
					this.hand_pos(d.c + 2, d.chand, rpox, rpoz);
					//opera.postError("AFTER hand_pos(" + (d.c + 2) + ", " + d.chand + ", " + rpox[0] + ", " + rpoz[0] + ")");

					if (tpox[0] != rpox[0] || tpoz[0] != rpoz[0]) {
						this.hand_pos(d.c + 1, d.chand, rpox, rpoz);
						if (tpox[0] != rpox[0] || tpoz[0] != rpoz[0]) d.st |= this.OBJECT_MOVE;
					}
				}
				else {
					this.hand_pos(d.c - 2, d.chand, tpox, tpoz);
					this.hand_pos(d.c, d.chand, rpox, rpoz);
					if (tpox[0] != rpox[0] || tpoz[0] != rpoz[0]) {
						this.hand_pos(d.c - 1, d.chand, tpox, tpoz);
						if (tpox[0] != rpox[0] || tpoz[0] != rpoz[0]) d.st |= this.OBJECT_MOVE;
					}
				}
			}
		}

		if (d.st & this.OBJECT_MOVE) {
			if (d.bh == 1) {
				this.hand_pos(d.c0 + 1, d.thand, tpox, tpoz);
				this.hand_pos(d.c + 1, d.chand, rpox, rpoz);
			}
			else if (d.st & this.OBJECT_UNDER) {
				this.hand_pos(d.c, d.chand, tpox, tpoz);
				this.hand_pos(d.c + 1, d.chand, rpox, rpoz);
			}
			else {
				this.hand_pos(d.c0 + 1, d.thand, tpox, tpoz);
				this.hand_pos(d.c, d.chand, rpox, rpoz);
			}
		}

		if (this.fpu) {
			var fx;

			if (!(d.st & this.OBJECT_HAND) && d.c < 0) {
				if (tpox[0] == 0) {
					fx = 0;
					y = parseInt(tpoz[0] * this.dpm / 20 - tp * this.dpm / 12 / this.tw);
					//opera.postError("x=" + x + ", y=" + y);
					//opera.postError(y, tpoz[0], this.dpm, tp, this.dpm, this.tw);
				}
				else {
					if (tpox[0] > 0) fx = tpox[0] / 10 - tp / 6 / this.tw;
					else fx = tpox[0] / 10 + tp / 6 / this.tw;
					y = parseInt(tpoz[0] * this.dpm / 20);
				}
			}
			else if (!(d.st & this.OBJECT_MOVE)) {
				fx = tpox[0] / 10;
				y = (tpoz[0] * this.dpm / 20);
			}
			else {
				if (d.bh == 1) {
					fx = (tp - this.aw) / this.tw * 2 + 1;
					y = parseInt(this.high[1] * (1 - fx ** 2));
				}
				else if (d.st & this.OBJECT_UNDER) {
					fx = tp / this.aw * 2 - 1;
					y = parseInt(this.high[0] * (1 - fx ** 2));
				}
				else {
					fx = tp / (this.tw * Math.abs(d.bh) - this.aw) * 2 + 1;
					y = parseInt(this.high[Math.abs(d.bh)] * (1 - fx ** 2));
				}

				y += parseInt((fx * (rpoz[0] - tpoz[0]) + rpoz[0] + tpoz[0]) * this.dpm / 40);
				d.t = fx; // spin
				fx = (fx * (rpox[0] - tpox[0]) + rpox[0] + tpox[0] ) / 20;
			}

			x = parseInt(fx * this.dpm * this.KW);
		}
		else {
			if (!(d.st & this.OBJECT_HAND) && d.c < 0) {
				if (tpox[0] == 0) {
					x = 0;
					y = parseInt(tpoz[0] * this.dpm / 20 - tp * this.dpm / 12 / this.tw);
				}
				else {
					if (tpox[0] > 0) x = parseInt(this.X_AXIS_ACCURACY * tpox[0] / 10 - this.X_AXIS_ACCURACY * tp / 6 / this.tw);
					else x = parseInt(this.X_AXIS_ACCURACY * tpox[0] / 10 + this.X_AXIS_ACCURACY * tp / 6 / this.tw);
					y = parseInt(tpoz[0] * this.dpm / 20);
				}
			}
			else if (!(d.st & this.OBJECT_MOVE)) {
				x = parseInt(this.X_AXIS_ACCURACY * tpox[0] / 10);
				y = parseInt(tpoz[0] * this.dpm / 20);
			}
			else {
				if (d.bh == 1) {
					x = parseInt(this.X_AXIS_ACCURACY * (tp - this.aw) * 2 / this.tw + this.X_AXIS_ACCURACY);
					y = parseInt(this.X_AXIS_ACCURACY ** 2 - x ** 2 / this.high0[1]);
				}
				else if (d.st & this.OBJECT_UNDER) {
					x = parseInt(this.X_AXIS_ACCURACY * tp * 2 / this.aw - this.X_AXIS_ACCURACY);
					y = parseInt(this.X_AXIS_ACCURACY ** 2 - x ** 2 / this.high0[0]);
				}
				else {
					x = parseInt(this.X_AXIS_ACCURACY * tp * 2 / (this.tw * Math.abs(d.bh) - this.aw) + this.X_AXIS_ACCURACY);
					y = parseInt(this.X_AXIS_ACCURACY ** 2 - x ** 2 / this.high0[Math.abs(d.bh)]);
				}

				y += parseInt((x * (rpoz[0] - tpoz[0]) + this.X_AXIS_ACCURACY * (rpoz[0] + tpoz[0])) * this.dpm / this.X_AXIS_ACCURACY / 40);
				x = parseInt((x * (rpox[0] - tpox[0]) + this.X_AXIS_ACCURACY * (rpox[0] + tpox[0])) / 20);
			}

			x = parseInt(x * this.dpm / this.kw0);
		}

		// NOTE:
		//  * The alternative calulations of d->gx and gy below are
		//    from JMWin. They cause the entire pattern to be skewed.

		//opera.postError(d);

		d.gx = this.horCenter + x - 11;
		//d->gx=horCenter + x - 11 * dpm / DW;
		//opera.postError(d);
		//opera.postError("hand_x=" + this.hand_x + " hand_y=" + this.hand_y);

		if (d.st & this.OBJECT_HAND) {
			if (d.chand) d.gx += this.hand_x;
			else d.gx -= this.hand_x;
			y -= this.hand_y;
		}

		d.gy = this.base - y - 11;
		//d->gy = base - y - 11 * dpm / DW;

		return flag;
	};

	set_ini (rr) {
		var i, j;
		var tw0;
		var aw0;

		this.ballNumber = 0;
		this.pmax = 0;

		if (this.pattw > this.MAX_COLUMN) return 10;

		if (this.pattw == 0) return 1;

		for (i = 0; i < this.pattw; i++) {
			for (j = 0; j < this.patts[i]; j++) {
				this.ballNumber += Math.abs(this.pattern[i][j]);
				this.pmax = Math.max(this.pmax, Math.abs(this.pattern[i][j]));
			}
		}

		if (this.ballNumber % this.pattw) return 5;
		this.ballNumber = parseInt(this.ballNumber / this.pattw);
		if (this.ballNumber == 0) return 9;
		if (this.ballNumber > this.MAX_BALLS) return 9;

		for (i = 0; i < this.MAX_COLUMN * 2; i++)
			this.r[i] = 0;

		for (i = 0; i <= this.ballNumber; i++) {
			j = 0;
			while (this.r[j] == this.patts[j % this.pattw] && j < this.pattw + this.pmax)
				j++;

			if (i == this.ballNumber) {
				if (j == this.pattw + this.pmax) break;
				else return 5;
			}

			this.balls[i].st = 0;

			if (this.mirror) {
				if ((j + this.synchronous) % 2) {
					this.balls[i].thand = 1;
					this.balls[i].chand = 1;
				}
				else {
					this.balls[i].thand = 0;
					this.balls[i].chand = 0;
				}
			}
			else {
				if ((j + this.synchronous) % 2) {
					this.balls[i].thand = 0;
					this.balls[i].chand = 0;
				}
				else {
					this.balls[i].thand = 1;
					this.balls[i].chand = 1;
				}
			}

			if (this.synchronous) this.balls[i].c = -(parseInt(j / 2)) * 2;
			else this.balls[i].c = -j;

			while (j < this.pattw + this.pmax) {
				if (this.r[j] == this.patts[j % this.pattw]) return 5;
				else this.r[j]++;
				var k = this.pattern[j % this.pattw][this.patts[j % this.pattw] - this.r[j]];

				if (this.synchronous && k < 0) {
					if (j % 2 == 0) j += -k + 1;
					else j += -k - 1;
				}
				else {
					j += k;
				}
			}
		}

		if (rr == 0) return 0;

		if (this.pmax < 3) this.pmax = 3;

		tw0 = Math.sqrt(2 / this.ga * this.pmax * this.heightRatio)
			* 2 / (this.pmax - this.dwellRatio * 2) * this.smode / this.cSpeed;
		this.tw = parseInt(this.fadd(tw0, 0, 0));
		if (this.tw == 0) return 15;
		aw0 = tw0 * this.dwellRatio * 2;
		this.aw = parseInt(this.fadd(aw0, 0, 0));
		if (this.aw < 1) this.aw = 1;
		if (this.aw > this.tw * 2 - 1) this.aw = this.tw * 2 - 1;

		this.kw0 = parseInt(this.X_AXIS_ACCURACY / this.KW);

		if (this.fpu) {
			this.high[0] = -0.2 * this.dpm;
			this.high[1] = this.ga * (tw0 / this.smode * this.cSpeed) ** 2 / 8 * this.dpm;
			for (i = 2; i <= this.pmax; i++)
				this.high[i] = this.ga * ((tw0 * i - aw0) / this.smode * this.cSpeed)**2 / 8 * this.dpm;
		}
		else {
			this.high0[0] = parseInt(-(this.X_AXIS_ACCURACY**2) / 0.2 * this.dpm);
			this.high0[1] = parseInt(this.X_AXIS_ACCURACY**2 / (this.ga * (tw0 / this.smode * this.cSpeed) / 8 * this.dpm));
			for (i = 2; i <= this.pmax; i++)
				this.high0[i] = parseInt(this.X_AXIS_ACCURACY**2 /
					(this.ga * ((tw0 * i - aw0) / this.smode * this.cSpeed) ** 2 / 8 * this.dpm));
		}

		for (i = 0; i < this.ballNumber; i++) {
			this.balls[i].bh = 0;
			this.balls[i].gx = this.horCenter;
			this.balls[i].gy = this.verCenter;
			this.balls[i].gx0 = this.horCenter;
			this.balls[i].gy0 = this.verCenter;
			this.balls[i].gx1 = this.horCenter;
			this.balls[i].gy1 = this.verCenter;
		}

		if (this.mirror) {
			this.handBall2.c = 0;
			if (this.synchronous) this.handBall1.c = 0;
			else this.handBall1.c = -1;
		}
		else {
			this.handBall1.c = 0;
			if (this.synchronous) this.handBall2.c = 0;
			else this.handBall2.c = -1;
		}

		this.handBall1.bh = 2;
		this.handBall1.st = this.OBJECT_HAND;
		this.handBall1.thand = 1;
		this.handBall1.chand = 1;
		this.handBall1.gx = this.horCenter;
		this.handBall1.gy = this.verCenter;
		this.handBall1.gx0 = this.horCenter;
		this.handBall1.gy0 = this.verCenter;
		this.handBall1.gx1 = this.horCenter;
		this.handBall1.gy1 = this.verCenter;

		this.handBall2.bh = 2;
		this.handBall2.st = this.OBJECT_HAND;
		this.handBall2.thand = 0;
		this.handBall2.chand = 0;
		this.handBall2.gx = this.horCenter;
		this.handBall2.gy = this.verCenter;
		this.handBall2.gx0 = this.horCenter;
		this.handBall2.gy0 = this.verCenter;
		this.handBall2.gx1 = this.horCenter;
		this.handBall2.gy1 = this.verCenter;

		for (i = 0; i < this.pattw; i++) this.r[i] = 0;

		return 0;
	};

	set_dpm () {
		var cSpeed0;

		cSpeed0 = this.cSpeed;
		this.cSpeed = 2.0;
		this.base = 0;
		this.dpm = 400;

		this.gy_max = 80 - 11;
		this.gy_min = -200 - 11;
		this.gx_max = -1000;
		this.gx_min = 1000;

		if (this.set_ini(1) == 0) {
			for (this.time_count = 0; this.time_count < this.tw * (this.pattw + this.pmax + this.style.length); this.time_count++) {
				for (var i = 0; i < this.ballNumber; i++) {
					this.juggle(this.balls[i]);
					this.gy_max = Math.max(this.gy_max, this.balls[i].gy);
					this.gy_min = Math.min(this.gy_min, this.balls[i].gy);
					this.gx_max = Math.max(this.gx_max, this.balls[i].gx + 2 * 11 * this.dpm / this.DW); // changed from X11 version
					this.gx_min = Math.min(this.gx_min, this.balls[i].gx);
				}

				this.juggle(this.handBall1);
				this.juggle(this.handBall2);

				this.gy_max = Math.max(this.gy_max, this.handBall1.gy);
				this.gy_min = Math.min(this.gy_min, this.handBall1.gy);
				this.gy_max = Math.max(this.gy_max, this.handBall2.gy);
				this.gy_min = Math.min(this.gy_min, this.handBall2.gy);

				this.gx_max = Math.max(this.gx_max, this.handBall1.gx);
				this.gx_min = Math.min(this.gx_min, this.handBall1.gx);
				this.gx_max = Math.max(this.gx_max, this.handBall2.gx);
				this.gx_min = Math.min(this.gx_min, this.handBall2.gx);

				this.arm_x = parseInt((22 - 11) * this.dpm / this.DW);
				this.arm_y = parseInt((16 - 11) * this.dpm / this.DW);

				// from JMWin:
				//ap.rx[0]=rhand.gx +11*dpm/DW+arm_x;
				//ap.ry[0]=rhand.gy +11*dpm/DW+arm_y;
				//ap.lx[0]=lhand.gx +11*dpm/DW-arm_x;
				//ap.ly[0]=lhand.gy +11*dpm/DW+arm_y;
				//

				this.arm_line();

				for (i = 0; i < 5; i++) {
					this.gx_max = Math.max(this.gx_max, this.arm.rx[i]);
					this.gx_max = Math.max(this.gx_max, this.arm.lx[i]);
					this.gx_min = Math.min(this.gx_min, this.arm.rx[i]);
					this.gx_min = Math.min(this.gx_min, this.arm.lx[i]);
				}
			}
		}

		if (this.gy_max - this.gy_min > 0) {
			// special handling for smaller screens
			if (this.imageWidth <= 320) {
				if (this.imageWidth > 160) { // 160-320 width
					this.dpm = parseInt(this.imageHeight * 280 / (this.gy_max - this.gy_min));
					this.base = parseInt(this.imageHeight - this.gy_max * this.dpm / this.imageHeight - 5);
				}
				else { // 0-160 width
					this.dpm = parseInt(this.imageHeight * 280 / (this.gy_max - this.gy_min));
					this.base = parseInt(this.imageHeight - this.imageHeight / 4);
				}
			}
			else {
				this.dpm = parseInt(400.0 * (this.imageHeight - 30 * 2) / (this.gy_max - this.gy_min));
				this.base = parseInt(this.imageHeight - 30 - this.gy_max * this.dpm / 400);
			}

			this.gx_min = parseInt(this.horCenter - (this.horCenter - this.gx_min) * this.dpm / 400);
			this.gx_max = parseInt(this.horCenter - (this.horCenter - this.gx_max) * this.dpm / 400);

			// original version
			//dpm=(JML_INT32)(400.0*340/(gy_max-gy_min));
			//if(dpm>DW) dpm=DW;
			//base=370-(JML_INT32)( (JML_FLOAT)gy_max*dpm/400 );
		}

		this.cSpeed = cSpeed0;
	};

	set_patt (s) {
		var flag = 0;
		var flag2 = 0;
		var a = 0;
		var pos = 0;

		if (s.length > this.MAX_COLUMN) return 10;

		this.pattw = 0;
		this.ballNumber = 0;

		if (s.charAt(0) == '(') this.synchronous = 1;
		else this.synchronous = 0;

		for (var i = 0; i < this.MAX_COLUMN; i++) {
			if (i >= s.length) {
				if (flag != 0 || flag2 != 0) return 4;
				break;
			}

			if (s.charAt(pos) == '[') {
				flag2 = 1;
				this.patts[this.pattw] = 0;
				pos++;
				continue;
			}
			if (s.charAt(pos) == ']') {
				if (flag2 == 0) return 4;
				flag2 = 0;
				this.pattw++;
				pos++;
				continue;
			}

			if (this.synchronous == 1) {
				switch (s.charAt(pos)) {
					case '(':
						if (flag != 0) return 4;
						flag = 1;
						pos++;
						continue;
					case ')':
						if (flag != 5) return 4;
						flag = 0;
						pos++;
						continue;
					case ',':
						if (flag != 2) return 4;
						flag = 4;
						pos++;
						continue;
					case 'X':
					case 'x':
						if (flag != 2 && flag != 5) return 4;
						if (flag2) this.pattern[this.pattw][this.patts[this.pattw] - 1] = -a;
						else this.pattern[this.pattw - 1][0] = -a;
						pos++;
						continue;
				}
			}

			a = this.ctod(s.charAt(pos));
			if (a == -1) return 6;

			if (this.synchronous) {
				if (a % 2) return 7;
				if (flag2 == 0 && flag != 1 && flag != 4) return 4;
				if (flag == 1) flag = 2;
				if (flag == 4) flag = 5;
			}

			if (flag2) {
				if (a == 0) return 13;
				this.pattern[this.pattw][this.patts[this.pattw]++] = a;
				if (this.patts[this.pattw] > this.MAX_MULTIPLEX) return 8;
			}
			else {
				this.pattern[this.pattw][0] = a;
				if (a == 0) this.patts[this.pattw++] = 0;
				else this.patts[this.pattw++] = 1;
			}
			pos++;
			this.ballNumber += a;
		}

		if (this.pattw == 0) return 9;
		this.ballNumber = parseInt(this.ballNumber / this.pattw);

		if (this.ballNumber > this.MAX_BALLS) return 9;

		return 0;
	};

// fixme: this function won't work
// use similar function from validator instead
/*
	ctod (c) {
	if      (c >= '0' && c <= '9') return c - '0';
	else if (c >= 'a' && c <= 'z') return c - 'a' + 10;
	else if (c >= 'A' && c <= 'Z') return c - 'A' + 10;

	return -1;
	}*/

	ctod (s) {
		var str_0 = new String("0");
		var str_a = new String("a");
		var str_A = new String("A");

		if (s >= '0' && s <= '9')
			return s.charCodeAt(0) - str_0.charCodeAt(0);
		else if (s >= 'a' && s <= 'z')
			return s.charCodeAt(0) - str_a.charCodeAt(0) + 10;
		else if (s >= 'A' && s <= 'Z')
			return s.charCodeAt(0) - str_A.charCodeAt(0) + 10;
		else
			return -1;
	};

	startJuggle () {
		this.set_dpm();
		if (this.set_ini(1) != 0)
			return;
		this.xbitset();

		// apply corrections
		this.bm1 = 11 - parseInt(11 * this.dpm / this.DW);
		this.time_count = 0;
		this.time_period = 0;

		this.status = this.STATUS_JUGGLE;
	};

	stopJuggle () {
		this.status = this.STATUS_NONE;
	};

	togglePause () {
		if (this.status == this.STATUS_JUGGLE)
			this.status = this.STATUS_PAUSE;
		else if (this.status == this.STATUS_PAUSE)
			this.status = this.STATUS_JUGGLE;
	};

	setPause (pauseOn) {
		if (this.status != this.STATUS_NONE) {
			if (pauseOn)
				this.status = this.STATUS_PAUSE;
			else
				this.status = this.STATUS_JUGGLE;
		}
	};
// fixme: xpos function seems to be wrong
	fadd (x, k, t) {
		return ((x + t) * 10 ** k + .5) / 10 ** k;
	};

	speedUp () {
		this.cSpeed = this.SPEED_MAX;
		this.set_ini(0);
	};

	speedDown () {
		this.cSpeed = this.SPEED_MIN;
		this.set_ini(0);
	};

	speedReset () {
		this.cSpeed = this.SPEED_DEFAULT;
		this.set_ini(0);
	};

	setSpeed (s) {
		this.cSpeed = s;
		this.set_ini(0);
	};
	doJuggle () {
		// Juggle 1 animation frame
		var i, tone = 0;

		if (this.status == this.STATUS_PAUSE || this.status == this.STATUS_NONE) {
			return 0;
		}

		this.time_count++;

		if (this.time_count < this.aw) this.time_count = this.aw;

		this.time_period = parseInt((this.time_count - this.aw) / this.tw);
		this.time_period %= this.pattw;
		//this.time_period = time_count % pattw;

		for (i = 0; i < this.ballNumber; i++)
			if (this.juggle(this.balls[i]) && this.beep) tone = Math.max(tone, Math.abs(this.balls[i].bh));

		if (this.juggle(this.handBall1) + this.juggle(this.handBall2) > 0) {
			//if (back_on==1) patt_print(1);
		}

		this.arm_line();
		this.applyCorrections();

		if (this.scalingMethod == this.SCALING_METHOD_DYNAMIC)
			this.doCoordTransform();

		return tone;
	};

	xbitset () {
		var i, j = 0;

		// data is used to create the hand bitmaps
		var data = [
			0, 18, 0, 23, 17, 23, 20, 22,
			22, 20, 23, 17, 23, 12, 18, 12,
			18, 16, 16, 18, 0, 18,
			12, 15, 23, 17, 99, 99,
		];

		// initialize the data array.
		for (i = 0; data[i] < 99; i++) data[i] = parseInt((data[i] - 11) * this.dpm / this.DW);

		// apply hand placement offsets
		this.hand_x = data[i - 4] + 2;
		this.hand_y = data[i - 3] + 2;
		this.arm_x = data[i - 2];
		this.arm_y = data[i - 1];

		// calculate hand polygons
		for (i = 0; data[i + 6] < 99; i += 2) {
			if (j > 9) break;

			this.hand2.rx[j] = 11 + data[i];
			this.hand2.ry[j] = 10 + data[i + 1];

			this.hand2.lx[j] = 12 - data[i];
			this.hand2.ly[j] = 10 + data[i + 1];

			j++;
		}

		for (i = 0; i <= 9; i++) {
			this.hand1.rx[i] = this.hand2.rx[i];
			this.hand1.ry[i] = this.hand2.ry[i];
			this.hand1.lx[i] = this.hand2.lx[i];
			this.hand1.ly[i] = this.hand2.ly[i];
		}
	};

	doStepcalc () {
		var i;
		var stp = 0; // position in steps array
		var pos = 0; // position in string

		// reset
		for (i = 0; i < this.MAX_COLUMN; i++) {
			this.steps[i] = -1;
		}

		// Synchronous pattern
		if (this.synchronous) {
			while (pos <= this.siteswap.length) {
				if (this.siteswap.charAt(pos) == '(') {
					this.steps[stp] = pos;
					stp += 2;
					while (this.siteswap.charAt(pos) != ')') {
						pos++;
					}
					pos++;
				}
				else if (pos == this.siteswap.length) {
					this.steps[stp] = pos;
					break;
				}
				else {
					this.error("Internal error");
					return;
				}
			}
		}
		else {
			while (pos <= this.siteswap.length) {
				if (this.siteswap.charAt(pos) == '(') {
					this.error("Internal error");
					return;
				}
				// Multiplex
				else if (this.siteswap.charAt(pos) == '[') {
					this.steps[stp++] = pos;
					while (this.siteswap.charAt(pos) != ']') {
						pos++;
					}
					pos++;
				}
				// Normal throw
				else {
					this.steps[stp++] = pos++;
				}
			}
		}
	}
}
